import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Trophy, RefreshCw, Send, History, Trash2 } from "lucide-react";

/**
 * Admin panel for the "Trader of the Week" weekly Telegram broadcast.
 * Renders a live preview, a "Post now" button (with force option),
 * and the last 5 audit rows.
 */
export default function TraderOfWeekCard() {
    const [preview, setPreview] = useState(null);
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(false);
    const [posting, setPosting] = useState(false);

    const loadPreview = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get("/admin/trader-of-week/preview");
            setPreview(r.data);
        } catch (e) {
            toast.error("Preview failed: " + (e?.response?.data?.detail || e.message));
        } finally {
            setLoading(false);
        }
    }, []);

    const loadHistory = useCallback(async () => {
        try {
            const r = await api.get("/admin/trader-of-week/history?limit=5");
            setHistory(r.data.rows || []);
        } catch (e) {
            // Silent — non-critical panel section
        }
    }, []);

    useEffect(() => { loadPreview(); loadHistory(); }, [loadPreview, loadHistory]);

    const postNow = async (force = false) => {
        setPosting(true);
        try {
            const r = await api.post(`/admin/trader-of-week/post-now${force ? "?force=true" : ""}`);
            const d = r.data;
            if (d.skipped) {
                toast.info(`Skipped: ${d.skip_reason}`);
            } else if (d.dispatch?.mocked) {
                toast.success(`Mocked dispatch OK for ${d.week_key} — configure TELEGRAM_BOT_TOKEN + TELEGRAM_CHANNEL_ID to go live.`);
            } else if (d.dispatch?.ok) {
                toast.success(`Posted to Telegram · week ${d.week_key} · message_id ${d.dispatch.message_id}`);
            } else {
                toast.error(`Dispatch failed: ${d.dispatch?.error || "unknown"}`);
            }
            await loadHistory();
        } catch (e) {
            toast.error("Post failed: " + (e?.response?.data?.detail || e.message));
        } finally {
            setPosting(false);
        }
    };

    const winners = preview?.payload?.winners || {};
    const top3 = preview?.payload?.top3_by_net_profit || [];
    const fb = preview?.payload?.fallback_used || {};

    return (
        <div data-testid="tow-card" className="space-y-4">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h3 className="text-2xl font-extrabold tracking-tight flex items-center gap-2">
                        <Trophy className="w-5 h-5 text-primary" /> Trader of the Week
                    </h3>
                    <p className="text-sm text-muted-foreground mt-1">
                        Auto-posts every <span className="mono">Sunday 20:00 EAT</span> to your Telegram channel with the featured OG share card, top-3 leaderboard, and a link to the public <span className="mono">/watch</span> tape.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={loadPreview}
                        disabled={loading}
                        data-testid="tow-refresh-btn"
                    >
                        <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} />
                        Refresh preview
                    </Button>
                    <Button
                        size="sm"
                        onClick={() => postNow(false)}
                        disabled={posting}
                        data-testid="tow-post-now-btn"
                    >
                        <Send className="w-4 h-4 mr-1" /> Post now
                    </Button>
                    <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => postNow(true)}
                        disabled={posting}
                        data-testid="tow-force-post-btn"
                        title="Bypass the already-posted-this-week guard (upsert)"
                    >
                        Force post
                    </Button>
                </div>
            </div>

            {preview && (
                <>
                    {/* Winner grid */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="tow-winners-grid">
                        {["net_profit", "win_rate", "consistency"].map((k) => {
                            const w = winners[k];
                            const isFallback = fb[k];
                            return (
                                <div
                                    key={k}
                                    className="border border-border bg-card rounded p-4"
                                    data-testid={`tow-winner-${k}`}
                                >
                                    <div className="overline text-xs text-muted-foreground mb-1 flex items-center gap-2">
                                        {k === "net_profit" ? "Net Profit"
                                            : k === "win_rate" ? "Win Rate"
                                                : "Consistency"}
                                        {isFallback && (
                                            <span className="text-[10px] px-1 py-0.5 rounded bg-amber-500/20 text-amber-600">
                                                fallback
                                            </span>
                                        )}
                                    </div>
                                    {w ? (
                                        <>
                                            <div className="font-extrabold text-lg tracking-tight" data-testid={`tow-winner-${k}-name`}>
                                                {w.name}
                                            </div>
                                            <div className="mono text-sm text-foreground/80 mt-1">
                                                {w.metric_value}
                                            </div>
                                            <div className="text-xs text-muted-foreground mt-1">
                                                {w.trades} trades · {w.unique_days} trading days · tier: {w.tier}
                                            </div>
                                        </>
                                    ) : (
                                        <div className="text-sm text-muted-foreground">No qualifying trader this week.</div>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {/* Top-3 by Net Profit */}
                    {top3.length > 0 && (
                        <div className="border border-border bg-card rounded p-4" data-testid="tow-top3">
                            <div className="overline text-xs text-muted-foreground mb-2">Top 3 by Net Profit (all-time snapshot)</div>
                            <div className="grid grid-cols-3 gap-3">
                                {top3.map((r) => (
                                    <div key={r.rank} data-testid={`tow-top3-rank-${r.rank}`}>
                                        <div className="text-xs text-muted-foreground">Rank {r.rank}</div>
                                        <div className="font-semibold">{r.name}</div>
                                        <div className="mono text-sm">KES {r.net_profit.toLocaleString("en-KE", { maximumFractionDigits: 0 })}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Caption preview */}
                    <div className="border border-border bg-card rounded p-4" data-testid="tow-caption-preview">
                        <div className="overline text-xs text-muted-foreground mb-2">Telegram caption preview (HTML)</div>
                        <pre className="text-xs whitespace-pre-wrap font-mono text-foreground/80 max-h-56 overflow-auto">
                            {preview.caption}
                        </pre>
                    </div>

                    {/* Delta email preview */}
                    {preview.delta_email && (
                        <div className="border border-border bg-card rounded p-4" data-testid="tow-delta-email">
                            <div className="overline text-xs text-muted-foreground mb-2">Weekly delta email (piggy-backed)</div>
                            {preview.delta_email.first_week ? (
                                <div className="text-sm text-amber-600">
                                    First week — no baseline snapshot yet.
                                    <span className="text-muted-foreground"> The next Sunday run will produce actual delta emails.</span>
                                </div>
                            ) : (
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                                    <div>
                                        <div className="text-xs text-muted-foreground">Candidates</div>
                                        <div className="font-extrabold text-xl" data-testid="tow-delta-candidates">{preview.delta_email.candidates}</div>
                                    </div>
                                    <div>
                                        <div className="text-xs text-muted-foreground">Previous week</div>
                                        <div className="font-mono">{preview.delta_email.previous_week_key || "\u2014"}</div>
                                    </div>
                                    <div>
                                        <div className="text-xs text-muted-foreground">Sent (last real run)</div>
                                        <div className="font-mono">{preview.delta_email.sent}</div>
                                    </div>
                                    <div>
                                        <div className="text-xs text-muted-foreground">Skipped</div>
                                        <div className="font-mono">{preview.delta_email.skipped}</div>
                                    </div>
                                </div>
                            )}
                            {preview.delta_email.dry_run_samples && preview.delta_email.dry_run_samples.length > 0 && (
                                <div className="mt-3 space-y-1 text-xs">
                                    <div className="text-muted-foreground">Sample subjects (first 5):</div>
                                    {preview.delta_email.dry_run_samples.map((s, i) => (
                                        <div key={s.user_id || `sample-${i}`} className="mono truncate">
                                            <span className="text-muted-foreground">{s.email}</span> — {s.subject}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </>
            )}

            {/* History */}
            <div className="border border-border bg-card rounded p-4" data-testid="tow-history">
                <div className="mb-2 flex items-center justify-between gap-2">
                    <div className="overline text-xs text-muted-foreground flex items-center gap-2">
                        <History className="w-3 h-3" /> Last 5 dispatches
                    </div>
                    {history.length > 0 && (
                        <Button
                            size="sm"
                            variant="ghost"
                            className="text-xs h-6 text-muted-foreground hover:text-destructive"
                            data-testid="tow-clear-history-btn"
                            onClick={async () => {
                                // Clears the trader_of_week_posts collection. Useful before
                                // going live to drop seeded / demo winners. Next Sunday's
                                // cron rebuilds fresh rows from real leaderboard activity.
                                if (!window.confirm("Clear all Trader-of-Week dispatch history? Next Sunday run will rebuild fresh.")) return;
                                try {
                                    const r = await api.delete("/admin/trader-of-week/history");
                                    toast.success(`Cleared ${r.data.deleted} dispatch row${r.data.deleted === 1 ? "" : "s"}.`);
                                    await loadHistory();
                                } catch (e) {
                                    toast.error("Clear failed: " + (e?.response?.data?.detail || e.message));
                                }
                            }}
                        >
                            <Trash2 className="w-3 h-3 mr-1" /> Clear history
                        </Button>
                    )}
                </div>
                {history.length === 0 ? (
                    <div className="text-sm text-muted-foreground">No dispatches recorded yet.</div>
                ) : (
                    <div className="space-y-2 text-sm">
                        {history.map((h) => (
                            <div key={h.week_key} className="flex items-center justify-between border-b border-border/50 py-1 last:border-0" data-testid={`tow-history-${h.week_key}`}>
                                <div>
                                    <span className="mono font-semibold">{h.week_key}</span>
                                    <span className="text-muted-foreground"> · {h.featured_name || "no winner"}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    {h.forced && <span className="text-[10px] px-1 py-0.5 rounded bg-amber-500/20 text-amber-600">forced</span>}
                                    {h.dispatch_mocked && <span className="text-[10px] px-1 py-0.5 rounded bg-muted text-muted-foreground">mocked</span>}
                                    {h.dispatch_ok && !h.dispatch_mocked && <span className="text-[10px] px-1 py-0.5 rounded bg-emerald-500/20 text-emerald-600">live</span>}
                                    {!h.dispatch_ok && !h.dispatch_mocked && <span className="text-[10px] px-1 py-0.5 rounded bg-red-500/20 text-red-600">failed</span>}
                                    <span className="text-xs text-muted-foreground">{new Date(h.posted_at).toLocaleString()}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
